import { useCallback } from 'react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { Button, ButtonAppearance, Icon, Text } from '@atlassian/learning-components';
import { Flex } from '@atlaskit/primitives/compiled';
import { css } from '@compiled/react';
import WarningIcon from '@atlaskit/icon/core/warning';

import { useEnrollInSession } from 'src/api/hooks/useEnrollInSession';
import { useUnenrollFromSession } from 'src/api/hooks/useUnenrollFromSession';
import { type OtherSession, type SessionData } from 'src/types/classSession';
import { generateGoogleCalendarUrl } from 'src/utils/calendar';

import { ClassTimeSlot } from './ClassTimeSlot';
import { FindOtherClasses } from './FindOtherClasses';

interface Props {
  courseId: string;
  sessionId: string;
  courseName: string;
  isEnrolled: boolean;
  enrollmentId?: string;
  isFree?: boolean;
  hasConflict: boolean;
  otherSessions: OtherSession[];
  sessionInfo: Omit<SessionData, 'id' | 'duration' | 'instructorNames'>;
}

const CALENDAR_DESCRIPTION = 'This is a calendar event to remind you that you booked a class with Atlassian Learning.';


export const LowerSection = ({
  courseId,
  sessionId,
  courseName,
  isEnrolled,
  enrollmentId,
  isFree,
  hasConflict,
  otherSessions,
  sessionInfo,
}: Props) => {
  const { month, day, label, time, dateStart, dateEnd, location } = sessionInfo;
  const { mutateAsync: enrollInSession, isPending: isEnrollLoading } = useEnrollInSession();
  const { mutateAsync: unenrollFromSession, isPending: isUnenrollLoading } = useUnenrollFromSession();

  const onEnrollForFreeClick = useCallback(async () => {
    if (!isFree || isEnrollLoading || isEnrolled) {
      return;
    }

    await enrollInSession(
      { courseId, sessionId },
      {
        onError: (error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to enroll in session', error);
        },
      },
    );
  }, [courseId, enrollInSession, isEnrollLoading, isEnrolled, isFree, sessionId]);

  const onUnenrollClick = useCallback(async () => {
    if (!isFree || !isEnrolled || isUnenrollLoading || !enrollmentId) {
      return;
    }

    await unenrollFromSession(
      { courseId, enrollmentId },
      {
        onError: (error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to unenroll from session', error);
        },
      },
    );
  }, [courseId, enrollmentId, isEnrolled, isFree, isUnenrollLoading, unenrollFromSession]);

  // TODO: Implement this
  const onEnrollPartnerClick = () => {};

  const onAddToCalendarClick = useCallback(() => {
    if (dateStart && dateEnd) {
      const googleCalendarUrl = generateGoogleCalendarUrl({
        title: courseName,
        startDateTime: dateStart,
        endDateTime: dateEnd,
        description: CALENDAR_DESCRIPTION,
        calendarType: 'google',
        videoCallUrl: location ?? undefined,
      });

      window.open(googleCalendarUrl, '_blank', 'noopener,noreferrer');
    }
  }, [courseName, dateStart, dateEnd, location]);

  const onFreeButtonClick = isEnrolled ? onUnenrollClick : onEnrollForFreeClick;
  const onEnrollClick = isFree ? onFreeButtonClick : onEnrollPartnerClick;

  const getButtonLabel = (): string => {
    if (!isFree) {
      return 'Enroll on a partner site';
    }

    return isEnrolled ? 'Unenroll' : 'Enroll for free';
  };

  return (
    <div css={lowerSectionContainerStyles}>
      <div css={slotActionsContainerStyles}>
        <ClassTimeSlot iconMonth={month} iconDay={day} titleDate={label} subtitleTime={time} />
        <div css={buttonContainerStyles}>
          {isEnrolled && (
            <Button
              label="Add to calendar"
              appearance={ButtonAppearance.LINK}
              iconAfter={<Icon type="add" color={token('color.icon.brand')} />}
              onClick={onAddToCalendarClick}
            />
          )}
          <FindOtherClasses courseName={courseName} sessions={otherSessions} />
          <Button
            label={getButtonLabel()}
            appearance={ButtonAppearance.PRIMARY}
            onClick={onEnrollClick}
            isLoading={Boolean(isFree && (isEnrolled ? isUnenrollLoading : isEnrollLoading))}
          />
        </div>
      </div>
      {hasConflict && (
        <Flex alignItems="center" gap="space.050">
          <WarningIcon label="warning" color={token('color.icon.warning')} />
          <Text>You’re enrolled in another class that conflicts with this time</Text>
        </Flex>
      )}
    </div>
  );
};

const slotActionsContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: token('space.400'),

  [media.above.sm]: {
    flexDirection: 'row',
    gap: token('space.400'),
  },
});

const buttonContainerStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.200'),
  alignItems: 'center',
});

const lowerSectionContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.400'),

  [media.above.sm]: {
    gap: token('space.200'),
  },
});
